import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  CheckCircle, 
  Clock, 
  Pause, 
  XCircle, 
  TrendingUp,
  Calendar,
  Filter,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOrderProgress = () => {
  const [progressData, setProgressData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // New filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [workOrderFilter, setWorkOrderFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState(""); // Customer name filter
  const [showNewWorkOrders, setShowNewWorkOrders] = useState(true);
  const [showOldWorkOrders, setShowOldWorkOrders] = useState(true);
  
  // Pagination states
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (workOrderFilter) params.append('work_order_number', workOrderFilter);
      
      const response = await axios.get(`${API}/reports/work-order-progress?${params.toString()}`);
      setProgressData(response.data);
    } catch (error) {
      console.error("Error fetching progress data:", error);
      toast.error("Failed to load work order progress");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = useCallback(() => {
    let filtered = [...progressData];

    // Filter by work order type (new vs legacy)
    if (!showNewWorkOrders) {
      filtered = filtered.filter(wo => wo.type !== 'new');
    }
    if (!showOldWorkOrders) {
      filtered = filtered.filter(wo => wo.type !== 'old');
    }

    // Filter by status (only for new work orders)
    if (statusFilter !== "all") {
      filtered = filtered.filter(wo => {
        if (wo.type === 'new') {
          return wo.status === statusFilter;
        } else if (wo.type === 'old') {
          // For old work orders, filter machine progress
          const filteredMachineProgress = wo.machine_progress?.filter(
            machine => machine.entries?.some(entry => entry.work_order_status === statusFilter)
          ) || [];
          return filteredMachineProgress.length > 0;
        }
        return true;
      });
    }

    // Filter by work order number (local filter on top of backend)
    if (workOrderFilter) {
      filtered = filtered.filter(wo => 
        wo.work_order_number.toLowerCase().includes(workOrderFilter.toLowerCase())
      );
    }

    // Filter by customer name
    if (customerFilter) {
      filtered = filtered.filter(wo => 
        wo.customer_name?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [progressData, statusFilter, workOrderFilter, customerFilter, showNewWorkOrders, showOldWorkOrders]);

  // Auto-filter when dependencies change
  useEffect(() => {
    applyFilter();
  }, [applyFilter]);

  const handleApplyFilters = () => {
    fetchProgressData();
  };

  const handleResetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setWorkOrderFilter("");
    setCustomerFilter("");
    setStatusFilter("all");
    fetchProgressData();
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: "bg-green-500/20", text: "text-green-300", border: "border-green-500/40", icon: CheckCircle },
      in_progress: { bg: "bg-blue-500/20", text: "text-blue-300", border: "border-blue-500/40", icon: Clock },
      on_hold: { bg: "bg-cyan-500/20", text: "text-cyan-300", border: "border-gray-600/40", icon: Pause },
      rejected: { bg: "bg-red-500/20", text: "text-red-300", border: "border-red-500/40", icon: XCircle },
    };

    const labels = {
      completed: "Completed",
      in_progress: "In Progress",
      on_hold: "On Hold",
      rejected: "Rejected",
    };

    const style = styles[status] || styles.in_progress;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
        <Icon className="h-3 w-3" />
        {labels[status] || "Unknown"}
      </span>
    );
  };

  const getDeadlineForMachine = (machineDeadlines, machineId) => {
    const deadline = machineDeadlines?.find(md => md.machine_id === machineId);
    if (deadline?.deadline) {
      return new Date(deadline.deadline).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    return "-";
  };

  // Flatten data for table view
  const getTableRows = () => {
    const rows = [];
    filteredData.forEach(wo => {
      if (wo.type === 'new') {
        // Get selected processes from the processes object returned by API
        const selectedProcesses = [];
        if (wo.processes) {
          for (let i = 1; i <= 10; i++) {
            const processKey = `process${i}`;
            if (wo.processes[processKey] === true) {
              selectedProcesses.push(processKey);
            }
          }
        }
        
        // Calculate completion percentage based on process_work_status
        const totalSelected = selectedProcesses.length;
        let processesWithCompletedWork = 0;
        
        // Check process_work_status for processes with completed work
        if (wo.process_work_status) {
          selectedProcesses.forEach(processKey => {
            const status = wo.process_work_status[processKey];
            if (status && status.completed_count > 0) {
              processesWithCompletedWork++;
            }
          });
        }
        
        // Fall back to completed_processes array if available
        const completedFromArray = (wo.completed_processes || []).filter(cp => 
          selectedProcesses.includes(cp)
        ).length;
        
        // Use the higher value between process_work_status and completed_processes
        const completedCount = Math.max(processesWithCompletedWork, completedFromArray);
        
        const completionPercentage = totalSelected > 0 
          ? Math.round((completedCount / totalSelected) * 100) 
          : 0;
        
        // Handle new work orders
        rows.push({
          work_order_number: wo.work_order_number,
          customer_name: wo.customer_name,
          item_name: wo.item_name,
          required_quantity: wo.required_quantity,
          delivery_date: wo.delivery_date,
          creation_date: wo.creation_date,
          completion_percentage: completionPercentage,
          total_processes: totalSelected,
          selected_processes: selectedProcesses,
          completed_processes: Array.isArray(wo.completed_processes) ? wo.completed_processes : [],
          status: wo.status,
          type: 'new',
          processes: wo.processes,
          timesheet_summary: wo.timesheet_summary,  // Include timesheet summary
          process_work_status: wo.process_work_status  // Include process work status
        });
      } else {
        // Handle old work orders
        wo.machine_progress?.forEach(machine => {
          machine.entries?.forEach(entry => {
            rows.push({
              work_order_number: wo.work_order_number,
              po_number: wo.po_number,
              machine_name: machine.machine_name,
              machine_id: machine.machine_id,
              deadline: getDeadlineForMachine(wo.machine_deadlines, machine.machine_id),
              employee_name: entry.employee_name,
              date: entry.date,
              start_time: entry.start_time,
              end_time: entry.end_time,
              work_order_status: entry.work_order_status,
              hours: entry.end_time !== "In Progress" ? calculateHours(entry.start_time, entry.end_time) : "-",
              type: 'old'
            });
          });
        });
      }
    });
    return rows;
  };

  const calculateHours = (start, end) => {
    if (end === "In Progress") return "-";
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const hours = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
    return hours.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
        <div className="text-center text-cyan-400">Loading...</div>
      </div>
    );
  }

  const tableRows = getTableRows();

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400 flex items-center gap-3">
              <TrendingUp className="h-8 w-8" />
              Work Order Progress Report
            </h1>
            <p className="text-gray-400 mt-1">
              Detailed tracking of work order progress across all machines
            </p>
          </div>
        </div>

      {/* Filter Section */}
      <Card className="bg-gray-800/50 border border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-cyan-400 flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
            <Button 
              onClick={fetchProgressData}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-cyan-300">Date From</Label>
              <div className="relative">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white pr-10 [color-scheme:dark]"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-400 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-cyan-300">Date To</Label>
              <div className="relative">
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white pr-10 [color-scheme:dark]"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-400 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-cyan-300">Work Order Number</Label>
              <Input
                type="text"
                placeholder="Search WO number..."
                value={workOrderFilter}
                onChange={(e) => setWorkOrderFilter(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-cyan-300">Customer Name</Label>
              <Input
                type="text"
                placeholder="Search customer..."
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-cyan-300">Status Filter</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600 text-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">✅ Completed</SelectItem>
                  <SelectItem value="in_progress">🔄 In Progress</SelectItem>
                  <SelectItem value="on_hold">⏸️ On Hold</SelectItem>
                  <SelectItem value="rejected">❌ Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Work Order Type Filters */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showNew"
                checked={showNewWorkOrders}
                onChange={(e) => setShowNewWorkOrders(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="showNew" className="text-cyan-300">Show New Work Orders</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showOld"
                checked={showOldWorkOrders}
                onChange={(e) => setShowOldWorkOrders(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="showOld" className="text-cyan-300">Show Legacy Work Orders</Label>
            </div>
            <Button 
              onClick={fetchProgressData}
              className="bg-green-600 hover:bg-green-700"
            >
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>

          <div className="text-gray-400 text-sm">
            Showing {progressData.length} work orders
          </div>
        </CardContent>
      </Card>

      {/* Table Report */}
      <Card className="bg-gray-800/50 border border-gray-700">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl text-cyan-400">Progress Details</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-gray-400 text-sm">Show:</Label>
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-8 bg-gray-900 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600 text-white">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-gray-400 text-sm">entries</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tableRows.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              No entries found for the selected filter
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-700 hover:bg-transparent">
                      <TableHead className="text-gray-300">WO Number</TableHead>
                      <TableHead className="text-gray-300">Type</TableHead>
                      <TableHead className="text-gray-300">Customer</TableHead>
                      <TableHead className="text-gray-300">Item</TableHead>
                      <TableHead className="text-gray-300">Quantity</TableHead>
                      <TableHead className="text-gray-300">Delivery Date</TableHead>
                      <TableHead className="text-gray-300">Progress</TableHead>
                      <TableHead className="text-gray-300">Work Status</TableHead>
                      <TableHead className="text-gray-300">Status</TableHead>
                      <TableHead className="text-gray-300">Processes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableRows.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((row, idx) => (
                    <TableRow 
                      key={row.work_order_number || `wo-row-${idx}`}
                      className="border-b border-gray-700 hover:bg-gray-800/30"
                    >
                      <TableCell className="font-semibold text-cyan-300">
                        {row.work_order_number}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.type === 'new' 
                            ? 'bg-green-500/20 text-green-300 border border-green-500/40' 
                            : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                        }`}>
                          {row.type === 'new' ? 'New' : 'Legacy'}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {row.customer_name || row.po_number || "-"}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {row.item_name || row.machine_name || "-"}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {row.required_quantity || row.machine_id || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-cyan-500" />
                          {row.delivery_date || row.deadline || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        {row.type === 'new' ? (
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-cyan-500 h-2 rounded-full transition-all"
                                style={{ width: `${row.completion_percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-cyan-400">
                              {row.completion_percentage}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            {row.employee_name || "-"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.type === 'new' && row.timesheet_summary && row.timesheet_summary.total_entries > 0 ? (
                          <div className="text-xs space-y-1">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              <span className="text-green-300">
                                {row.timesheet_summary.completed_entries || 0} completed
                              </span>
                            </div>
                            {(row.timesheet_summary.in_progress_entries > 0) && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-cyan-400" />
                                <span className="text-cyan-300">
                                  {row.timesheet_summary.in_progress_entries} in progress
                                </span>
                              </div>
                            )}
                            {(row.timesheet_summary.ongoing_entries > 0) && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-blue-400" />
                                <span className="text-blue-300">
                                  {row.timesheet_summary.ongoing_entries} ongoing
                                </span>
                              </div>
                            )}
                            <div className="text-gray-400">
                              {row.timesheet_summary.total_hours_worked || 0}h worked
                            </div>
                          </div>
                        ) : row.type === 'new' ? (
                          <span className="text-gray-400 text-xs">No timesheet data</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.type === 'new' ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            row.status === 'active' 
                              ? 'bg-green-500/20 text-green-300 border border-green-500/40' 
                              : 'bg-gray-500/20 text-gray-300 border border-gray-500/40'
                          }`}>
                            {row.status}
                          </span>
                        ) : (
                          getStatusBadge(row.work_order_status)
                        )}
                      </TableCell>
                      <TableCell>
                        {row.type === 'new' ? (
                          <div className="space-y-1">
                            <div className="flex flex-wrap gap-1">
                              <TooltipProvider delayDuration={100}>
                              {[
                                { key: 'process1', label: 'P1', fullName: 'Corrugation Machine' },
                                { key: 'process2', label: 'P2', fullName: 'Sheet / Paper Cutting Machine' },
                                { key: 'process2a', label: 'P2A', fullName: 'Sheet Cutting Machine' },
                                { key: 'process2b', label: 'P2B', fullName: 'Paper Cutting Machine' },
                                { key: 'process3', label: 'P3', fullName: 'Sheet Pasting Machine' },
                                { key: 'process4', label: 'P4', fullName: 'Thin Blade Slitter Scorer Machine' },
                                { key: 'process5', label: 'P5', fullName: '4 Bar Rotary Creasing Machine' },
                                { key: 'process6', label: 'P6', fullName: 'RS4 Single Color Printing & Slotting Machine' },
                                { key: 'process6a', label: 'P6A', fullName: 'Eccentric Slotter' },
                                { key: 'process7', label: 'P7', fullName: 'Flat bed Punching' },
                                { key: 'process8', label: 'P8', fullName: 'Rotary Die Punching - Single Color Print' },
                                { key: 'process9', label: 'P9', fullName: 'Calico Pasting Machine' },
                                { key: 'process9a', label: 'P9A', fullName: 'Calico Pasting Variant' },
                                { key: 'process10', label: 'P10', fullName: 'Stitching & Bundling Machine' },
                                { key: 'process11', label: 'P11', fullName: 'Box Packing Machine' },
                                { key: 'process12', label: 'P12', fullName: 'Dispatch / Loading' },
                              ].filter(process => {
                                // Show processes that were selected during creation
                                return row.processes && row.processes[process.key];
                              }).map(process => {
                                // Check completion from BOTH sources:
                                // 1. process_work_status (timesheet/clock-in — Pro)
                                // 2. completed_processes array (Material Usage manual entry — Core)
                                const workStatus = row.process_work_status && row.process_work_status[process.key];
                                const hasWork = workStatus?.has_work;
                                const isInCompletedArray = Array.isArray(row.completed_processes)
                                  && row.completed_processes.includes(process.key);
                                const hasCompleted = (workStatus?.completed_count > 0) || isInCompletedArray;
                                const hasOngoing = !hasCompleted && workStatus?.ongoing_count > 0;
                                
                                return (
                                  <Tooltip key={process.key}>
                                    <TooltipTrigger asChild>
                                      <span 
                                        className={`px-2 py-1 text-xs rounded border cursor-help ${
                                          hasCompleted
                                            ? 'bg-green-500/30 text-green-300 border-green-500 font-bold' 
                                            : hasOngoing
                                            ? 'bg-blue-500/30 text-blue-300 border-blue-500 font-semibold'
                                            : 'bg-gray-500/10 text-gray-500 border-gray-600'
                                        }`}
                                      >
                                        {hasCompleted ? '✓' : hasOngoing ? '⏳' : '○'} {process.label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="bg-gray-900 text-white border border-gray-600/50 max-w-xs">
                                      <div className="text-sm font-semibold text-cyan-400">{process.fullName}</div>
                                      {hasWork ? (
                                        <div className="text-xs mt-1">
                                          <span className="text-green-400">{workStatus.completed_count} completed</span>
                                          <span className="mx-1">|</span>
                                          <span className="text-blue-400">{workStatus.ongoing_count} ongoing</span>
                                          <span className="mx-1">|</span>
                                          <span className="text-gray-300">{workStatus.total_hours.toFixed(1)}h total</span>
                                        </div>
                                      ) : isInCompletedArray ? (
                                        <div className="text-xs text-emerald-300 mt-1">Completed (manual entry)</div>
                                      ) : (
                                        <div className="text-xs text-gray-400 mt-1">No work yet</div>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                              </TooltipProvider>
                              {(!row.processes || !Object.values(row.processes).some(p => p)) && (
                                <span className="text-gray-500 text-xs">No processes selected</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {row.hours !== "-" ? `${row.hours}h` : "Legacy"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Showing {Math.min((currentPage - 1) * pageSize + 1, tableRows.length)} to {Math.min(currentPage * pageSize, tableRows.length)} of {tableRows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <span className="text-cyan-400 px-3">
                  Page {currentPage} of {Math.ceil(tableRows.length / pageSize) || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(tableRows.length / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(tableRows.length / pageSize)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default WorkOrderProgress;
